/**
 * S-19 Store (V5 payments, SM-10) — v3 "AFTER-DARK ARCADE" restyle.
 *
 * Every number on this screen is SERVER truth: prices, balances, caps and
 * buyability come from GET /store/catalog (the server price table — PRD §7.4:
 * the client renders balances, never computes them). Buying redirects to a
 * Stripe-hosted Checkout Session; credit lands via webhook regardless of the
 * redirect (EC-27). Card disable states are server-driven (blocked_reason).
 *
 * v3: the store sells game power, so it leans arcade — per-verb neon motifs
 * on the SKU emblems (shield cyan · berserker fire · SR violet · boost gold ·
 * crush accent), an inventory HUD whose Anton counts pop on change, and a
 * granted BEAT (announcer sting + card bloom + soft shake) when a purchase
 * credits. SANDBOX_MODE (client-config.sandbox): a banner + "Sandbox — free"
 * buy treatment (price struck through, still visible); the checkout POST is
 * the SAME endpoint — the server grants free and we celebrate on the spot.
 * All selectors of the payments e2e are preserved (sku-*, buy-*, balance-*,
 * sub-status, manage-subscription).
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { copy, formatCopy, resolveCopy } from "@humble/shared";
import { api, isApiError, withEventId } from "../../lib/api.js";
import { ws } from "../../lib/ws.js";
import {
  announce,
  Button,
  Card,
  IconShield,
  IconSkull,
  rise,
  shakeScreen,
  Skeleton,
  springs,
  stagger,
  toast,
} from "../../design/index.js";
import { IconBolt, IconCrown, IconFlame, IconHourglass, IconStar } from "./sku-icons.js";
import "./store.css";

export interface CatalogItem {
  sku: string;
  price_cents: number;
  currency: string;
  qty: number;
  item: string | null;
  mode: "payment" | "subscription";
  balance: number;
  balance_cap: number | null;
  buyable: boolean;
  blocked_reason?: "cap_overflow" | "restricted" | "already_subscribed" | "lifecycle";
  subscription?: {
    status: string;
    entitled: boolean;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
  };
}

export function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Per-SKU art direction: copy + verb color token + emblem + announcer motif. */
interface SkuMeta {
  title: string;
  pitch: string;
  qtyLabel?: string;
  verb: string; // CSS custom property value for --verb
  motif: "gold" | "berserk" | "sr" | "accent";
  icon: ReactNode;
}

const SKU_META: Record<string, SkuMeta> = {
  shield_pack_3: {
    title: "Shields",
    qtyLabel: "3-pack",
    pitch: copy.store.shield_pitch,
    verb: "var(--shield)",
    motif: "gold",
    icon: <IconShield width={26} height={26} />,
  },
  berserker_1: {
    title: "Berserker Mode",
    qtyLabel: "8 hours",
    pitch: copy.store.berserker_pitch,
    verb: "var(--berserk)",
    motif: "berserk",
    icon: <IconFlame width={26} height={26} />,
  },
  boost_1: {
    title: "Boost",
    qtyLabel: "30 min",
    pitch: "Front of the deck for 30 minutes. Subtlety sold separately.",
    verb: "var(--gold)",
    motif: "gold",
    icon: <IconBolt width={26} height={26} />,
  },
  super_reject_5: {
    title: "Super Rejects",
    qtyLabel: "5-pack",
    pitch: "Five personal 24-hour timeouts. Aim responsibly.",
    verb: "var(--sr)",
    motif: "sr",
    icon: <IconSkull width={26} height={26} />,
  },
  crush_5: {
    title: "Crushes",
    qtyLabel: "5-pack",
    pitch: "The sincere one, in bulk. Feelings not included but likely.",
    verb: "var(--accent)",
    motif: "accent",
    icon: <IconStar width={26} height={26} />,
  },
  extend_1: {
    title: "Match Extend",
    qtyLabel: "+24 h",
    pitch: "Buy a conversation one more day. The fuse waits for no one — except money.",
    verb: "var(--like)",
    motif: "gold",
    icon: <IconHourglass width={26} height={26} />,
  },
  premium_monthly: {
    title: "Humble Premium",
    pitch: copy.store.premium_pitch,
    verb: "var(--sr)",
    motif: "sr",
    icon: <IconCrown width={26} height={26} />,
  },
};

const FALLBACK_META = (sku: string): SkuMeta => ({
  title: sku,
  pitch: "",
  verb: "var(--text-dim)",
  motif: "gold",
  icon: <IconStar width={26} height={26} />,
});

const ITEM_LABEL: Record<string, string> = {
  shield: "Shields",
  berserker: "Berserker",
  boost: "Boosts",
  super_reject: "Super Rejects",
  crush: "Crushes",
  extend: "Extends",
};

const ITEM_VERB: Record<string, string> = {
  shield: "var(--shield)",
  berserker: "var(--berserk)",
  boost: "var(--gold)",
  super_reject: "var(--sr)",
  crush: "var(--accent)",
  extend: "var(--like)",
};

function blockedLabel(it: CatalogItem): string | null {
  switch (it.blocked_reason) {
    case "restricted":
      return copy.store.restricted;
    case "cap_overflow":
      return it.balance >= (it.balance_cap ?? 0)
        ? formatCopy(copy.store.cap_reached, { item: ITEM_LABEL[it.item ?? ""] ?? it.item ?? "" })
        : formatCopy(copy.store.cap_overflow, {
            pack_qty: it.qty,
            balance: it.balance,
            cap: it.balance_cap ?? 0,
            overflow_needed: it.balance + it.qty - (it.balance_cap ?? 0),
          });
    case "lifecycle":
      return "Finish onboarding first — the store sells to real profiles only.";
    default:
      return null;
  }
}

/** Sandbox free-grant checkout response (payments.md — same guards, no Stripe). */
interface CheckoutResponse {
  checkout_id: string;
  stripe_url: string | null;
  sandbox?: boolean;
  granted?: boolean;
}

export function StoreScreen() {
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  const [busySku, setBusySku] = useState<string | null>(null);
  const [sandbox, setSandbox] = useState(false);
  const [grantedSku, setGrantedSku] = useState<string | null>(null);
  const grantTimer = useRef<number | undefined>(undefined);

  const load = useCallback(() => {
    api
      .get("/store/catalog")
      .then((rows) => setItems(rows as unknown as CatalogItem[]))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    load();
    // client-config.sandbox drives the free-grant treatment (flag only —
    // the server decides what checkout actually does).
    api
      .get("/client-config")
      .then((cfg) => setSandbox(Boolean((cfg as { sandbox?: boolean }).sandbox)))
      .catch(() => setSandbox(false));
    ws.ensureConnected();
    const offInv = ws.on("inventory.updated", load); // balances are re-fetched, never computed
    const offSub = ws.on("premium_changed", load);
    return () => {
      offInv();
      offSub();
      window.clearTimeout(grantTimer.current);
    };
  }, [load]);

  /** The granted BEAT — sting + soft shake + a bloom sweep on the card. */
  const celebrate = useCallback((sku: string) => {
    const meta = SKU_META[sku] ?? FALLBACK_META(sku);
    announce({
      sting: resolveCopy("store.granted_sting") ?? "GRANTED.",
      sub: resolveCopy("store.granted_sub"),
      motif: meta.motif,
    });
    shakeScreen("soft");
    setGrantedSku(null);
    window.clearTimeout(grantTimer.current);
    // re-mount the flash class next frame so repeat buys retrigger it
    requestAnimationFrame(() => setGrantedSku(sku));
    grantTimer.current = window.setTimeout(() => setGrantedSku(null), 1300);
  }, []);

  const buy = async (sku: string) => {
    setBusySku(sku);
    try {
      const body = withEventId<{ client_event_id: string; sku: string }>({ sku });
      const res = (await api.post("/store/checkout", {
        body: body as never,
      })) as unknown as CheckoutResponse;
      if (res.stripe_url) {
        window.location.href = res.stripe_url; // Stripe-hosted checkout hand-off
      } else if (res.sandbox) {
        // SANDBOX free grant — credited server-side already; celebrate + re-fetch.
        celebrate(sku);
        setBusySku(null);
        load();
      } else {
        toast("Checkout created but no redirect URL came back. Try again.", "error");
        setBusySku(null);
      }
    } catch (err) {
      toast(isApiError(err) ? err.message : "Checkout failed. Nothing was charged.", "error");
      setBusySku(null);
      load();
    }
  };

  const openPortal = async () => {
    setBusySku("premium_monthly");
    try {
      const res = (await api.post("/store/subscription/portal", {})) as unknown as { url: string };
      window.location.href = res.url;
    } catch (err) {
      toast(isApiError(err) ? err.message : "Could not open the billing portal.", "error");
      setBusySku(null);
    }
  };

  if (items === null) {
    return (
      <div className="store">
        <header className="store__head">
          <p className="h-caps store__kicker">The armory</p>
          <h1 className="type-display-3 store__title">Store</h1>
        </header>
        <div className="store__hud" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} height={52} radius={10} style={{ flex: "1 1 88px" }} />
          ))}
        </div>
        <Skeleton height={110} radius={20} style={{ marginBottom: 12 }} />
        <Skeleton height={110} radius={20} style={{ marginBottom: 12 }} />
        <Skeleton height={110} radius={20} />
      </div>
    );
  }

  const balances = items.filter((i) => i.item !== null);
  const featured = items.filter((i) => i.sku === "shield_pack_3" || i.sku === "berserker_1");
  const rest = items.filter(
    (i) => i.mode === "payment" && i.sku !== "shield_pack_3" && i.sku !== "berserker_1",
  );
  const premium = items.find((i) => i.sku === "premium_monthly");

  return (
    <motion.div className="store" variants={stagger} initial="initial" animate="enter">
      <motion.header className="store__head" variants={rise}>
        <p className="h-caps store__kicker">The armory</p>
        <h1 className="type-display-3 store__title">Store</h1>
        <p className="type-editorial store__lede">{resolveCopy("store.lede")}</p>
      </motion.header>

      {sandbox && (
        <motion.div className="store__sandbox" role="status" variants={rise} data-testid="sandbox-banner">
          <span className="store__sandbox-tag">Sandbox</span>
          <span>{resolveCopy("store.sandbox_banner")}</span>
        </motion.div>
      )}

      {/* Inventory HUD — server truth, top of screen (S-19) */}
      <motion.div className="store__hud" data-testid="balance-strip" variants={rise}>
        {balances.map((i) => (
          <div
            key={i.sku}
            className="store__stock"
            style={{ "--verb": ITEM_VERB[i.item!] ?? "var(--text)" } as CSSProperties}
          >
            <span className="store__stock-label">{ITEM_LABEL[i.item!] ?? i.item}</span>
            <span className="store__stock-count">
              <strong data-testid={`balance-${i.item!}`}>
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.span
                    key={i.balance}
                    initial={{ scale: 1.5, opacity: 0, y: -4 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.6, opacity: 0, y: 4 }}
                    transition={springs.bouncy}
                    style={{ display: "inline-block", fontWeight: 400 }}
                  >
                    {i.balance}
                  </motion.span>
                </AnimatePresence>
              </strong>
              {i.balance_cap !== null && <span className="store__stock-cap"> / {i.balance_cap}</span>}
            </span>
          </div>
        ))}
      </motion.div>

      {/* Featured: Shield 3-pack + Berserker */}
      <div className="store__section">
        {featured.map((it) => (
          <motion.div key={it.sku} variants={rise}>
            <SkuCard
              it={it}
              busy={busySku === it.sku}
              sandbox={sandbox}
              granted={grantedSku === it.sku}
              onBuy={buy}
              featured
            />
          </motion.div>
        ))}
      </div>

      <div className="store__section">
        {rest.map((it) => (
          <motion.div key={it.sku} variants={rise}>
            <SkuCard
              it={it}
              busy={busySku === it.sku}
              sandbox={sandbox}
              granted={grantedSku === it.sku}
              onBuy={buy}
            />
          </motion.div>
        ))}
      </div>

      {premium && (
        <motion.div variants={rise}>
          <PremiumCard
            it={premium}
            busy={busySku === "premium_monthly"}
            sandbox={sandbox}
            granted={grantedSku === "premium_monthly"}
            onBuy={buy}
            onPortal={openPortal}
          />
        </motion.div>
      )}
    </motion.div>
  );
}

/** Buy label: normal = the price; sandbox = struck price + FREE (the price
 *  stays in the text — server truth remains visible, and the e2e checks it). */
function BuyLabel({ cents, sandbox, suffix }: { cents: number; sandbox: boolean; suffix?: string }) {
  if (!sandbox) {
    return (
      <>
        {formatPrice(cents)}
        {suffix}
      </>
    );
  }
  return (
    <>
      <s className="sku__was">
        {formatPrice(cents)}
        {suffix}
      </s>
      <span className="sku__free">Free</span>
    </>
  );
}

function SkuCard({
  it,
  busy,
  sandbox,
  granted,
  onBuy,
  featured = false,
}: {
  it: CatalogItem;
  busy: boolean;
  sandbox: boolean;
  granted: boolean;
  onBuy: (sku: string) => void;
  featured?: boolean;
}) {
  const meta = SKU_META[it.sku] ?? FALLBACK_META(it.sku);
  const blocked = blockedLabel(it);
  const glow = it.sku === "berserker_1" ? "berserk" : featured ? "gold" : "none";
  return (
    <Card
      padding="lg"
      glow={glow}
      className={granted ? "sku--granted" : undefined}
      data-testid={`sku-${it.sku}`}
      style={{ "--verb": meta.verb } as CSSProperties}
    >
      <div className="sku">
        <div className="sku__emblem" aria-hidden="true">
          {meta.icon}
        </div>
        <div className="sku__body">
          <div className="sku__title">
            <span>{meta.title}</span>
            {meta.qtyLabel && <span className="sku__qty">{meta.qtyLabel}</span>}
          </div>
          <p className="sku__pitch">{meta.pitch}</p>
          {it.sku === "berserker_1" && <p className="sku__fine">{copy.berserk.store_honesty}</p>}
          {blocked && (
            <p className="sku__blocked" data-testid={`blocked-${it.sku}`}>
              {blocked}
            </p>
          )}
        </div>
        <div className="sku__buy">
          <Button
            onClick={() => onBuy(it.sku)}
            disabled={!it.buyable}
            loading={busy}
            data-testid={`buy-${it.sku}`}
          >
            <BuyLabel cents={it.price_cents} sandbox={sandbox} />
          </Button>
          {sandbox && <span className="sku__sandbox-note">{resolveCopy("store.sandbox_free")}</span>}
        </div>
      </div>
    </Card>
  );
}

function PremiumCard({
  it,
  busy,
  sandbox,
  granted,
  onBuy,
  onPortal,
}: {
  it: CatalogItem;
  busy: boolean;
  sandbox: boolean;
  granted: boolean;
  onBuy: (sku: string) => void;
  onPortal: () => void;
}) {
  const sub = it.subscription;
  const isSubscriber = sub !== undefined && sub.status !== "none" && sub.status !== "lapsed";
  const meta = SKU_META.premium_monthly!;
  return (
    <Card
      padding="lg"
      glow="sr"
      className={`store__premium${granted ? " sku--granted" : ""}`}
      data-testid="sku-premium_monthly"
      style={{ "--verb": meta.verb } as CSSProperties}
    >
      <div className="sku">
        <div className="sku__emblem" aria-hidden="true">
          {meta.icon}
        </div>
        <div className="sku__body">
          <div className="sku__title">
            <span>Humble Premium</span>
            <span className="sku__qty">{formatPrice(it.price_cents)}/mo</span>
          </div>
          <p className="sku__pitch">{copy.store.premium_pitch}</p>
          <ul className="sku__perks">
            <li>See your Fans and your Haters — names, not fog.</li>
            <li>150 likes and rejects a day. 3 Super Rejects, 3 Crushes.</li>
            <li>Filter like a snob. Passport anywhere. Incognito browsing.</li>
          </ul>
          {isSubscriber ? (
            <div>
              <p className="store__sub-status" data-testid="sub-status">
                Status: <strong>{sub!.status}</strong>
                {sub!.current_period_end
                  ? ` · ${sub!.cancel_at_period_end || sub!.status === "cancel_scheduled" ? "ends" : "renews"} ${new Date(sub!.current_period_end).toLocaleDateString()}`
                  : ""}
              </p>
              <Button variant="ghost" onClick={onPortal} loading={busy} data-testid="manage-subscription">
                Manage subscription
              </Button>
            </div>
          ) : (
            <div className="sku__buy" style={{ alignSelf: "auto", alignItems: "flex-start" }}>
              <Button
                variant="sr"
                onClick={() => onBuy(it.sku)}
                disabled={!it.buyable}
                loading={busy}
                data-testid="buy-premium_monthly"
              >
                Subscribe · <BuyLabel cents={it.price_cents} sandbox={sandbox} suffix="/mo" />
              </Button>
              {sandbox && (
                <span className="sku__sandbox-note">{resolveCopy("store.sandbox_free")}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
